import React, { useMemo, useCallback, useEffect, useContext } from 'react';
import { DatePicker } from 'antd';
import type { DatePickerProps as AntDatePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import { useFieldValidation } from '../../base/useFieldValidation';
import { deriveScopedComponentId, mergeStoreProps } from '../../base/storePropsUtils';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import { resolveDatePickerValueFormat } from '../../utils/resolveDatePickerValueFormat';
import { ValidateHelp } from '../../base/ValidateHelp';
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from '../../types/common';
import { DataSetInheritContext, useResolvedDataSet } from '../../base/DataSetInheritContext';

dayjs.extend(customParseFormat);

const DEFAULT_STORE_FORMAT = 'YYYY-MM-DD';

/**
 * AndDatePicker Props 接口
 */
export interface AndDatePickerProps extends Omit<AntDatePickerProps, 'onChange' | 'value'>, BaseComponentProps, DataSetConfig, ValidationDisplayConfig {
  /** 变更动作 */
  changeAction?: string;
  /** 值格式 (如 'YYYY-MM-DD') */
  valueFormat?: string;
  /** 校验规则 */
  rules?: any[];
  /** 失焦时校验 */
  validateOnBlur?: boolean;
  /** 修改时校验 */
  validateOnChange?: boolean;
  /** 自定义变更处理函数 */
  onChange?: (date: Dayjs | null, dateString: string | string[]) => void;
  /** 值 */
  value?: string | Dayjs;
  /** 迭代器内行下标，供继承 dataSet 与 changeAction 中 @index 占位 */
  index?: number;
}

/**
 * AndDatePicker 组件
 * 封装 Ant Design DatePicker，支持数据绑定和动作解析
 */
export const AndDatePicker: React.FC<AndDatePickerProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  changeAction,
  valueFormat: valueFormatProp,
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  onChange,
  value: propValue,
  defaultValue,
  index,
  ...restProps
}) => {
  const resolved = useResolvedDataSet(dataSet, dataSetField, sessionId);
  const effectiveDataSet = resolved.dataSet;
  const effectiveDataSetField = resolved.dataSetField;
  const effectiveSessionId = resolved.sessionId;

  const ctx = useContext(DataSetInheritContext);
  const effectiveIndex = index ?? ctx?.index;
  const effectiveParentIndex = ctx?.parentIndex;

  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );

  /** 与 AndSelect 一致：迭代器内 propsStore 使用带下标的作用域 id */
  const derivedPropsComponentId = useMemo(
    () => (effectiveSessionId ? deriveScopedComponentId(id, ctx, index) : null),
    [effectiveSessionId, id, ctx?.parentIndex, ctx?.index, index]
  );
  const storeProps = useAndStore((s) =>
    derivedPropsComponentId
      ? s.propsStore[`${effectiveSessionId}_props`]?.[derivedPropsComponentId] ?? null
      : null
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && effectiveDataSetField
      ? s.getDataSet(dataSetKey, effectiveDataSetField)
      : undefined
  );

  const { validate, help } = useFieldValidation({
    rules,
    dataSet: effectiveDataSet,
    dataSetField: effectiveDataSetField,
    sessionId: effectiveSessionId,
    id,
    develop,
  });

  const mergedProps = useMemo(
    () => mergeStoreProps(restProps as Record<string, any>, storeProps ?? undefined),
    [restProps, storeProps]
  );

  const datePickerDomProps = useMemo(
    () => prepareAntdDomProps(mergedProps as Record<string, unknown>),
    [mergedProps],
  );

  const displayFormat =
    typeof mergedProps.format === 'string' ? mergedProps.format : undefined;

  const effectiveValueFormat = useMemo(
    () =>
      resolveDatePickerValueFormat({
        valueFormat: valueFormatProp,
        format: displayFormat,
        showTime: mergedProps.showTime === true,
      }),
    [valueFormatProp, displayFormat, mergedProps.showTime],
  );

  const parseDateValue = useCallback(
    (val: any): Dayjs | null => {
      if (val === null || val === undefined || val === '') return null;
      if (dayjs.isDayjs(val)) return val.isValid() ? val : null;
      if (typeof val === 'number' && Number.isFinite(val)) {
        const parsed = dayjs(val);
        return parsed.isValid() ? parsed : null;
      }
      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed && /^\d+$/.test(trimmed)) {
          const num = Number(trimmed);
          const ms = trimmed.length === 10 ? num * 1000 : num;
          const parsed = dayjs(ms);
          if (parsed.isValid()) return parsed;
        }
      }
      if (typeof val !== 'string') return null;

      const formats = [effectiveValueFormat, DEFAULT_STORE_FORMAT, displayFormat].filter(
        (f): f is string => Boolean(f)
      );
      for (const fmt of formats) {
        const parsed = dayjs(val, fmt, true);
        if (parsed.isValid()) return parsed;
      }
      const loose = dayjs(val);
      return loose.isValid() ? loose : null;
    },
    [effectiveValueFormat, displayFormat]
  );

  const displayValue = useMemo(() => {
    const rawValue = storeValue ?? storeProps?.value ?? propValue ?? defaultValue;
    return parseDateValue(rawValue);
  }, [storeValue, storeProps?.value, propValue, defaultValue, parseDateValue]);

  const handleChange = useCallback(
    (date: Dayjs | null, dateString: string | string[]) => {
      if (onChange) {
        onChange(date, dateString);
        return;
      }
      const persistValue =
        date != null && date.isValid() ? date.format(effectiveValueFormat) : '';
      if (dataSetKey && effectiveDataSetField) {
        setDataSet(dataSetKey, effectiveDataSetField, persistValue);
      }
      if (validateOnChange) validate(persistValue || null);
      if (changeAction && effectiveSessionId) {
        const { actionName, arg } = parseChangeAction(changeAction, persistValue, {
          id,
          dataSet: effectiveDataSet,
          dataSetField: effectiveDataSetField,
          index: effectiveIndex,
          parentIndex: effectiveParentIndex,
        }, ctx);
        addCommand(effectiveSessionId, createCommand(actionName, arg));
      }
    },
    [
      onChange,
      dataSetKey,
      effectiveDataSetField,
      setDataSet,
      validateOnChange,
      validate,
      changeAction,
      effectiveSessionId,
      id,
      effectiveDataSet,
      effectiveDataSetField,
      effectiveIndex,
      effectiveParentIndex,
      addCommand,
      effectiveValueFormat,
      ctx,
    ]
  );

  useEffect(() => {
    if (defaultValue && displayValue === null && dataSetKey && effectiveDataSetField) {
      const formatted = dayjs(defaultValue as any).format(effectiveValueFormat);
      setDataSet(dataSetKey, effectiveDataSetField, formatted);
    }
  }, [
    defaultValue,
    displayValue,
    dataSetKey,
    effectiveDataSetField,
    setDataSet,
    effectiveValueFormat,
  ]);

  const handleBlur = useCallback(() => {
    if (validateOnBlur) {
      const raw = displayValue ? displayValue.format(effectiveValueFormat) : null;
      validate(raw);
    }
  }, [validateOnBlur, validate, displayValue, effectiveValueFormat]);

  return (
    <div onBlur={handleBlur} style={{ display: 'flex' }}>
      <DatePicker
        {...datePickerDomProps}
        disabled={develop ? false : (datePickerDomProps.disabled as boolean | undefined)}
        open={develop ? false : undefined}
        value={displayValue}
        onChange={handleChange as any}
        status={help ? ('error' as const) : undefined}
        style={{ width: '100%', ...(datePickerDomProps.style as React.CSSProperties) }}
      />
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );
};

export default AndDatePicker;
